/**
 * `EditWebhookEndpointModal` — ADR-0162 §3a `PATCH /v1/skills/:skill_id`.
 *
 * The dashboard front for changing a custom-webhook skill's
 * fulfillment endpoint. The origin re-probes on save; the returned
 * `endpoint_status` drives the row pill (`healthy` → mint; `unreachable`
 * → coral). Owner/admin only (server 403s otherwise).
 *
 * Mirrors the field styling used in `CustomSkillModal` — same `Modal`
 * / `Field` atoms, same raw-input styling, same server-reason mapping
 * for `fulfillment_endpoint_invalid` / `role_insufficient` / rate limit.
 */

import { useEffect, useState } from 'react';

import type { Skill, SiteId } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useC } from '../../lib/theme.js';
import { Field, Modal } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface EditWebhookEndpointModalProps {
  open: boolean;
  onClose: () => void;
  site_id: SiteId;
  skill: Skill;
  onSaved: (skill: Skill) => void;
}

function serverReasonCopy(err: ApiError): string {
  if (err.kind === 'rate_limited') {
    return 'Too many changes — try again in a moment.';
  }
  switch (err.server_reason) {
    case 'fulfillment_endpoint_invalid':
      return 'Endpoint must be HTTPS on your verified domain.';
    case 'role_insufficient':
      return 'Only owners and admins can edit endpoints.';
    default:
      break;
  }
  if (err.kind === 'bad_request') return 'Check the endpoint and try again.';
  if (err.kind === 'forbidden' || err.kind === 'not_found') {
    return 'You do not have permission to edit this skill.';
  }
  if (err.kind === 'service_unavailable') {
    return 'The service is temporarily unavailable. Try again in a moment.';
  }
  return 'Could not save the endpoint. Try again.';
}

export function EditWebhookEndpointModal({
  open,
  onClose,
  site_id,
  skill,
  onSaved,
}: EditWebhookEndpointModalProps): JSX.Element | null {
  const C = useC();
  const api = useApi();
  const [endpoint, setEndpoint] = useState(skill.fulfillment_endpoint ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEndpoint(skill.fulfillment_endpoint ?? '');
      setErrorMsg(null);
      setSubmitting(false);
    }
  }, [open, skill.fulfillment_endpoint]);

  const trimmed = endpoint.trim();
  const valid = trimmed.length > 0 && /^https:\/\//i.test(trimmed);
  // Dirty guard — the dialog is only "editing" when the merchant has
  // actually changed the endpoint from the server's current value.
  const dirty = endpoint !== (skill.fulfillment_endpoint ?? '');
  const canSubmit = valid && !submitting && dirty;

  const save = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await api.skills.patchWebhookEndpoint(
        site_id,
        skill.skill_id,
        trimmed,
      );
      onSaved(res.skill);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMsg(serverReasonCopy(err));
      } else {
        setErrorMsg('Could not save the endpoint. Try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Edit fulfillment endpoint"
      subtitle={skill.name}
      closeOnBackdrop={false}
      dirty={dirty}
    >
      <div className="p-6 space-y-4">
        <Field
          label="Fulfillment endpoint"
          hint="Must be an HTTPS URL on your verified domain. Sill re-probes on save."
          required
        >
          <input
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder="https://hooks.your-site.com/skills/..."
            className="w-full px-3 py-2.5 outline-none"
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              color: C.text,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          />
        </Field>
        {errorMsg !== null ? (
          <div
            role="alert"
            className="p-3"
            style={{
              background: C.coralDim,
              border: `1px solid rgba(255,107,71,0.3)`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              lineHeight: 1.5,
            }}
          >
            {errorMsg}
          </div>
        ) : null}
      </div>
      <div
        className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 px-6 py-4"
        style={{ borderTop: `1px solid ${C.border}` }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="px-4 py-2.5"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.muted,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            void save();
          }}
          disabled={!canSubmit}
          className="flex-1 sm:flex-none px-6 py-2.5"
          style={{
            background: canSubmit ? C.lime : C.surface2,
            color: canSubmit ? C.ink : C.muted,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            opacity: canSubmit ? 1 : 0.6,
          }}
        >
          {submitting ? 'Saving…' : 'Save endpoint'}
        </button>
      </div>
    </Modal>
  );
}
